import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { NavLink } from "@/components/NavLink";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn, getInitials } from "@/lib/utils";
import { useUnseenEngagementCount } from "@/hooks/useUnseenEngagement";
import { useLibraryCounts } from "@/hooks/useLibraryCounts";
import { ThemedLogo } from "@/components/shared/ThemedLogo";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import {
  Rss,
  BookOpen,
  BarChart3,
  Users as UsersIcon,
  LayoutDashboard,
  Shield,
  Users,
  FolderOpen,
  ChevronRight,
  Zap,
  Inbox,
  TrendingUp,
  Handshake,
  MapPin,
  Wallet,
  FlaskConical,
  Globe,
  RotateCcw,
  Brain,
  Flag,
  Sparkles,
  GitBranch,
} from "lucide-react";
import { FEATURE_COSMO_CHAT, FEATURE_COUNCIL } from "@/lib/featureFlags";

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  primary?: boolean;
}

interface LibrarySubItem {
  title: string;
  tab: string;
  count?: number;
}

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, membership } = useAuth();
  const [userName, setUserName] = useState<string>("");
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const { data: unseenCount = 0 } = useUnseenEngagementCount(user?.id);
  const { data: libraryCounts } = useLibraryCounts(user?.id);
  
  const [libraryOpen, setLibraryOpen] = useState(
    location.pathname.startsWith("/library")
  );
  const [councilOpen, setCouncilOpen] = useState(
    location.pathname.startsWith("/council")
  );
  const [execOpen, setExecOpen] = useState(
    location.pathname.startsWith("/exec-dashboard")
  );

  // Keep library open when on library page
  useEffect(() => {
    if (location.pathname.startsWith("/library")) {
      setLibraryOpen(true);
    }
    if (location.pathname.startsWith("/council")) {
      setCouncilOpen(true);
    }
    if (location.pathname.startsWith("/exec-dashboard")) {
      setExecOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, full_name")
        .eq("user_id", user.id)
        .single();

      if (data) {
        const firstName = data.first_name || "";
        const lastName = data.last_name || "";
        const combinedName = [firstName, lastName].filter(Boolean).join(" ");
        setUserName(combinedName || data.full_name || "User");
      }
    };

    fetchUserInfo();
  }, [user]);

  useEffect(() => {
    const fetchPoints = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .rpc("get_user_total_points", { _user_id: user.id });
      
      if (data !== null) {
        setTotalPoints(data);
      }
    };

    fetchPoints();
  }, [user]);

  const navItems: NavItem[] = [
    { title: "Discover Prompts", href: "/feed", icon: Rss, primary: true },
    { title: "My Stats", href: "/stats", icon: BarChart3 },
    { title: "Team Stats", href: "/organization", icon: UsersIcon },
  ];

  const librarySubItems: LibrarySubItem[] = [
    { title: "Recent", tab: "recent", count: libraryCounts?.recent },
    { title: "Favorites", tab: "favorites", count: libraryCounts?.favorites },
    { title: "Drafts", tab: "drafts", count: libraryCounts?.drafts },
  ];

  // Get current active tab from URL
  const currentTab = searchParams.get("tab") || "recent";
  const isLibraryActive = location.pathname.startsWith("/library");

  const handleLibrarySubClick = (tab: string) => {
    navigate(`/library?tab=${tab}`);
  };

  // Add exec/admin routes if user has permissions
  const additionalItems: NavItem[] = [];
  const canViewExecDashboard =
    membership?.role === "exec" ||
    membership?.role === "client_admin" ||
    membership?.role === "admin";
  if (membership?.role === "client_admin") {
    additionalItems.push({
      title: "Team",
      href: "/team",
      icon: Users,
    });
  }
  if (membership?.role === "admin") {
    additionalItems.push({
      title: "Admin",
      href: "/admin",
      icon: Shield,
    });
  }

  // Council nav items (client_admin + exec + admin)
  const isCouncilMember =
    membership?.role === "client_admin" ||
    membership?.role === "exec" ||
    membership?.role === "admin";

  // Groups for the collapsible council section (feature flagged)
  interface CouncilGroup { label?: string; items: NavItem[] }
  const councilGroups: CouncilGroup[] = (FEATURE_COUNCIL && isCouncilMember) ? [
    {
      items: [
        { title: "Momentum Engine",   href: "/council/momentum",        icon: Zap        },
        { title: "Recommendations",   href: "/council/recommendations",  icon: Inbox      },
        { title: "Maturity Journey",  href: "/council/maturity",         icon: TrendingUp },
        { title: "Analytics",         href: "/council/analytics",        icon: BarChart3  },
        { title: "AiA Partnership",   href: "/council/partnership",      icon: Handshake  },
      ],
    },
    {
      label: "Projects",
      items: [
        { title: "Initiatives",       href: "/council/initiatives",      icon: Flag       },
        { title: "Task Discovery",    href: "/council/discovery",        icon: RotateCcw  },
      ],
    },
    {
      label: "Tools",
      items: [
        { title: "Tool Portfolio",    href: "/council/tools",            icon: Wallet     },
        { title: "Tool Trials",       href: "/council/trials",           icon: FlaskConical },
        { title: "AI Landscape",      href: "/council/landscape",        icon: Globe      },
      ],
    },
    ...(membership?.role === "admin" ? [{
      label: "System",
      items: [
        { title: "Agentic Core",      href: "/council/agentic",          icon: Brain      },
      ],
    }] : []),
  ] : [];

  return (
    <aside className="w-64 h-screen sticky top-0 bg-sidebar border-r border-sidebar-border flex flex-col shrink-0">
      {/* Logo Section */}
      <div className="p-4 border-b border-sidebar-border">
        <button
          onClick={() => navigate("/feed")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <ThemedLogo variant="horizontal" className="h-10 w-auto object-contain" alt="Prompt Coach Logo" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Primary CTA */}
        <div className="mb-3">
          <Button
            variant="default"
            size="lg"
            className="w-full justify-between"
            onClick={() => navigate("/prompt")}
          >
            Build-A-Prompt
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* Cosmo — primary home (feature flagged) */}
        {FEATURE_COSMO_CHAT && (
          <NavLink
            to="/cosmo"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
            "text-foreground/85 hover:text-foreground hover:bg-sidebar-accent",
          )}
            activeClassName="bg-sidebar-accent text-foreground"
          >
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="flex-1">Cosmo</span>
          </NavLink>
        )}

        {/* Workflow Builder */}
        <NavLink
          to="/workflows"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
            "text-foreground/85 hover:text-foreground hover:bg-sidebar-accent",
          )}
          activeClassName="bg-sidebar-accent text-foreground"
        >
          <GitBranch className="h-5 w-5 text-primary" />
          <span className="flex-1">Workflows</span>
          <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary">Beta</span>
        </NavLink>

        {/* Collapsible Library Section */}
        <Collapsible open={libraryOpen} onOpenChange={setLibraryOpen}>
          <CollapsibleTrigger
            onClick={() => navigate("/library")}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
              "text-foreground/85 hover:text-foreground hover:bg-sidebar-accent",
              isLibraryActive && "bg-sidebar-accent text-foreground"
            )}
          >
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="flex-1 text-left">Vault</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                libraryOpen && "rotate-90"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 mt-1 space-y-0.5">
            {librarySubItems.map((subItem) => (
              <button
                key={subItem.tab}
                onClick={() => handleLibrarySubClick(subItem.tab)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  "text-foreground/75 hover:text-foreground hover:bg-sidebar-accent",
                  isLibraryActive && currentTab === subItem.tab && "bg-sidebar-accent/70 text-foreground"
                )}
              >
                <FolderOpen className="h-4 w-4" />
                <span className="flex-1 text-left">{subItem.title}</span>
                {subItem.count !== undefined && subItem.count > 0 && (
                  <span className="h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                    {subItem.count > 99 ? "99+" : subItem.count}
                  </span>
                )}
              </button>
            ))}
          </CollapsibleContent>
        </Collapsible>

        {/* Discover Prompts */}
        <NavLink
          to="/feed"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
            "text-foreground/85 hover:text-foreground hover:bg-sidebar-accent"
          )}
          activeClassName="bg-sidebar-accent text-foreground"
        >
          <Rss className="h-5 w-5 text-primary" />
          <span className="flex-1">Discover Prompts</span>
        </NavLink>

        {/* Stats and Team */}
        {navItems.slice(1).map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
              "text-foreground/85 hover:text-foreground hover:bg-sidebar-accent"
            )}
            activeClassName="bg-sidebar-accent text-foreground"
          >
            <item.icon className="h-5 w-5 text-primary" />
            <span className="flex-1">{item.title}</span>
            {item.href === "/stats" && unseenCount > 0 && (
              <span className="h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                {unseenCount > 99 ? "99+" : unseenCount}
              </span>
            )}
          </NavLink>
        ))}

        {/* Council section — collapsible */}
        {councilGroups.length > 0 && (
          <>
            <div className="h-px bg-sidebar-border my-3" />
            <Collapsible open={councilOpen} onOpenChange={setCouncilOpen}>
              <CollapsibleTrigger
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
                  "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent",
                  councilOpen && "text-foreground"
                )}
              >
                <Zap className="h-5 w-5 text-orange-400" />
                <span className="flex-1 text-left">Council</span>
                <ChevronRight
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    councilOpen && "rotate-90"
                  )}
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-3 mt-0.5 space-y-0">
                {councilGroups.map((group, gi) => (
                  <div key={gi}>
                    {group.label && (
                      <p className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/40">
                        {group.label}
                      </p>
                    )}
                    {group.items.map((item) => (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"
                        activeClassName="bg-sidebar-accent text-foreground"
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">{item.title}</span>
                      </NavLink>
                    ))}
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          </>
        )}

        {(canViewExecDashboard || additionalItems.length > 0) && (
          <>
            <div className="h-px bg-sidebar-border my-3" />
            {canViewExecDashboard && (
              <Collapsible open={execOpen} onOpenChange={setExecOpen}>
                <CollapsibleTrigger
                  onClick={() => navigate("/exec-dashboard")}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors",
                    "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent",
                    location.pathname.startsWith("/exec-dashboard") && "bg-sidebar-accent text-foreground"
                  )}
                >
                  <LayoutDashboard className="h-5 w-5" />
                  <span className="flex-1 text-left">Exec Dashboard</span>
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      execOpen && "rotate-90"
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 mt-1 space-y-0.5">
                  <button
                    onClick={() => navigate("/exec-dashboard/library")}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      "text-foreground/75 hover:text-foreground hover:bg-sidebar-accent",
                      location.pathname === "/exec-dashboard/library" && "bg-sidebar-accent/70 text-foreground"
                    )}
                  >
                    <BookOpen className="h-4 w-4" />
                    <span className="flex-1 text-left">Prompt Library</span>
                  </button>
                </CollapsibleContent>
              </Collapsible>
            )}
            {additionalItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"
                activeClassName="bg-sidebar-accent text-foreground"
              >
                <item.icon className="h-5 w-5" />
                {item.title}
              </NavLink>
            ))}
          </>
        )}

      </nav>

      {/* Profile Section */}
      <div className="p-3 border-t border-sidebar-border">
        <button
          onClick={() => navigate("/profile")}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
            "hover:bg-sidebar-accent",
            location.pathname === "/profile" && "bg-sidebar-accent"
          )}
        >
          <Avatar className="h-9 w-9 border-2 border-primary/20">
            <AvatarFallback className="bg-muted text-muted-foreground text-base">
              {getInitials(userName || user?.user_metadata?.full_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-base font-medium truncate">
              {userName || user?.user_metadata?.full_name || "User"}
            </span>
            <span className="text-sm font-semibold text-primary truncate">
              {totalPoints.toLocaleString()} pts
            </span>
          </div>
        </button>
      </div>
    </aside>
  );
}
